import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import { makeTheme, ThemeMode } from "../theme";

export type ThemePreference = "system" | "light" | "dark";

interface ThemeModeContextValue {
  preference: ThemePreference;
  resolved: ThemeMode;
  setPreference: (p: ThemePreference) => void;
  cycle: () => void;
}

const STORAGE_KEY = "minidash.theme";

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

const systemPrefersDark = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

const resolve = (pref: ThemePreference): ThemeMode => {
  if (pref === "system") return systemPrefersDark() ? "dark" : "light";
  return pref;
};

const loadPreference = (): ThemePreference => {
  if (typeof window === "undefined") return "system";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  return "system";
};

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(() =>
    loadPreference(),
  );
  const [resolved, setResolved] = useState<ThemeMode>(() =>
    resolve(loadPreference()),
  );

  // Keep `resolved` in sync with preference and (when preference=system) with
  // the user's system setting.
  useEffect(() => {
    const update = () => setResolved(resolve(preference));
    update();
    if (preference !== "system") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, [preference]);

  // Reflect resolved mode on the document element so tokens.css picks it up.
  useEffect(() => {
    const el = document.documentElement;
    el.setAttribute("data-theme", resolved);
    el.style.colorScheme = resolved;
  }, [resolved]);

  const setPreference = useCallback((p: ThemePreference) => {
    setPreferenceState(p);
    try {
      window.localStorage.setItem(STORAGE_KEY, p);
    } catch {
      // localStorage can throw in private-mode browsers; non-fatal.
    }
  }, []);

  const cycle = useCallback(() => {
    setPreferenceState((prev) => {
      const next: ThemePreference =
        prev === "system" ? "light" : prev === "light" ? "dark" : "system";
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  const muiTheme = useMemo(() => makeTheme(resolved), [resolved]);

  const value = useMemo<ThemeModeContextValue>(
    () => ({ preference, resolved, setPreference, cycle }),
    [preference, resolved, setPreference, cycle],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = (): ThemeModeContextValue => {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) {
    throw new Error("useThemeMode must be used inside ThemeModeProvider");
  }
  return ctx;
};
